import { AxiosInstance, AxiosResponse } from 'axios';
import { getAPIBaseURL } from '../../utilities/BaseURLUtility';
import { isUserAnalyticsEnabled } from '../configuration/config';

interface ApiMetric {
  url: string;
  method: string;
  duration: number;
  status: number;
  path: string;
  userId: string;
  timestamp: string;
  success: boolean;
  error?: string;
}

class ApiAnalyticsService {
  private static instance: ApiAnalyticsService;
  private currentPath = '';
  private currentUserId = '';
  private pendingRequests = 0;
  private metricsQueue: ApiMetric[] = [];
  private batchSize = 10;
  private flushInterval = 30000; // 30 seconds
  private flushTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.startBatchFlush();
  }

  public static getInstance(): ApiAnalyticsService {
    if (!ApiAnalyticsService.instance) {
      ApiAnalyticsService.instance = new ApiAnalyticsService();
    }
    return ApiAnalyticsService.instance;
  }

  public setContext(path: string, userId: string) {
    this.currentPath = path;
    this.currentUserId = userId;
  }

  public attachToAxiosInstance(axiosInstance: AxiosInstance) {
    // Request interceptor to track start time
    axiosInstance.interceptors.request.use(
      (config: any) => {
        config.metadata = { startTime: performance.now() };
        this.pendingRequests++;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor to track completion and measure duration
    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        this.recordMetric(response.config, response, null);
        if (this.pendingRequests > 0) this.pendingRequests--;
        return response;
      },
      (error) => {
        this.recordMetric(error.config, error.response, error);
        if (this.pendingRequests > 0) this.pendingRequests--;
        return Promise.reject(error);
      },
    );
  }

  private recordMetric(config: any, response: AxiosResponse | undefined, error: any) {
    if (!config.metadata?.startTime || !this.currentUserId) return;

    const duration = performance.now() - config.metadata.startTime;
    const url = this.getFullUrl(config);

    // Skip analytics endpoints to avoid circular calls
    if (url.includes('/Analytics/')) return;

    const metric: ApiMetric = {
      url,
      method: config.method?.toUpperCase() || 'GET',
      duration: Math.round(duration),
      status: response?.status || error?.response?.status || 0,
      path: this.currentPath,
      userId: this.currentUserId,
      timestamp: new Date().toISOString(),
      success: !!response && response.status >= 200 && response.status < 300,
      error: error ? this.getErrorMessage(error) : undefined,
    };

    this.queueMetric(metric);
  }

  private getFullUrl(config: any): string {
    const baseURL = config.baseURL || '';
    const url = config.url || '';
    return `${baseURL}${url}`.replace(/\/+/g, '/').replace(/:\//g, '://');
  }

  private getErrorMessage(error: any): string {
    if (error.response?.data?.message) return error.response.data.message;
    if (error.response?.statusText) return error.response.statusText;
    if (error.message) return error.message;
    return 'Unknown error';
  }

  private queueMetric(metric: ApiMetric) {
    this.metricsQueue.push(metric);

    if (this.metricsQueue.length >= this.batchSize) {
      this.flushMetrics();
    }
  }

  private async flushMetrics() {
    if (this.metricsQueue.length === 0) return;

    const metricsToSend = [...this.metricsQueue];
    this.metricsQueue = [];

    const isEnabled = await isUserAnalyticsEnabled();
    if (!isEnabled) {
      return;
    }

    try {
      // Use fetch instead of axios to avoid circular dependency
      await fetch(`${this.getApiBaseUrl()}/Analytics/api-performance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ metrics: metricsToSend }),
      });
    } catch (error) {
      console.warn('Failed to send API analytics:', error);
      // Don't re-queue failed metrics to avoid infinite loops
    }
  }

  private getApiBaseUrl(): string {
    return getAPIBaseURL();
  }

  private startBatchFlush() {
    this.flushTimer = setInterval(() => {
      this.flushMetrics();
    }, this.flushInterval);
  }

  public destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flushMetrics(); // Flush any remaining metrics
  }

  // Get performance summary for current page
  public getPageSummary(): { totalRequests: number; avgDuration: number; errorRate: number } {
    const pageMetrics = this.metricsQueue.filter((m) => m.path === this.currentPath);
    const totalRequests = pageMetrics.length;
    const avgDuration = totalRequests > 0 ? pageMetrics.reduce((sum, m) => sum + m.duration, 0) / totalRequests : 0;
    const errorRate = totalRequests > 0 ? pageMetrics.filter((m) => !m.success).length / totalRequests : 0;

    return { totalRequests, avgDuration, errorRate };
  }

  // Wait until all pending API requests complete
  public waitForIdle(): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        if (this.pendingRequests <= 0) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }
}

export default ApiAnalyticsService;
